/**
 * Charter claim metrics (M5 spec §5.1; plan C1): per (system × non-outlaw faction) operations value, security
 * strength and influence points, written into C0's `ClaimStore`. Pure writers: M4 code paths call
 * `accumulateOps` and `addInfluence` (C11 wires them), the slow tick calls `sampleSecurity`, and day slice 0
 * calls `rollMetrics`. Outlaws never score (every writer ignores them).
 *
 * Operations and security are EMAs over 3 faction days (`x ← emaKeep × x + (1 − emaKeep) × today`). Influence
 * is points that decay `decayPerDay` each faction day (`x ← (1 − decay) × x + today`), plus the daily sources
 * (reputation, and Holdfast in the Isolated Era) added by `rollMetrics`.
 *
 * M7 claim states (plan S18; spec §12.2, `story/claimStates.ts`): while a claim (or one of the corporation's stations
 * in the system) is `underReview`, the holder's (the corporation's) operations there count only votes.json
 * `story.claimStates.underReviewOpsMult`; while a claim is `harbouring`, its holder's security there counts only
 * `harbouringSecurityMult` (contestable). With every state `none` both multipliers are 1.
 *
 * Hot-loop rule (D-014): every function here allocates nothing.
 */
import { CLAIM_STATE, FACTION, INFLUENCE_SOURCE, ROLE, STAGE, type StrategicState } from '../types'

/** True when the claim in `sys` held by `faction`, or a corporation station of `faction` there, is in claim state `code`. */
function inClaimState(state: StrategicState, sys: number, faction: number, code: number): boolean {
  const st = state.story
  if (st.claimState[sys] === code && state.claims.holder[sys] === faction) return true
  if (faction !== state.corp.faction || faction < 0) return false
  const c = state.commissions
  for (let i = 0; i < c.high; i++) if (c.alive[i] === 1 && c.sys[i] === sys && st.commissionState[i] === code) return true
  return false
}

/** The share of `faction`'s operations in `sys` that counts (M7 `underReview`: votes.json `underReviewOpsMult`). */
export function opsCountMult(state: StrategicState, sys: number, faction: number): number {
  return inClaimState(state, sys, faction, CLAIM_STATE.underReview) ? state.data.charter.votes.story.claimStates.underReviewOpsMult : 1
}

/** The share of `faction`'s security in `sys` that counts (M7 `harbouring`: votes.json `harbouringSecurityMult`). */
export function securityCountMult(state: StrategicState, sys: number, faction: number): number {
  return inClaimState(state, sys, faction, CLAIM_STATE.harbouring) ? state.data.charter.votes.story.claimStates.harbouringSecurityMult : 1
}

/** True when `faction` can score: a charter faction in factions.json that is not an outlaw. */
export function canScore(state: StrategicState, faction: number): boolean {
  const c = state.data.charter
  return faction >= 0 && faction < c.factionCount && faction < state.claims.factionCapacity && c.outlaw[faction] === 0
}

/**
 * Adds `value` credits of operations to today's accumulator (production output value, mined ore value,
 * habitat service value, or trade throughput already scaled by `tradeOpsValue`). Outlaws and non-positive
 * values are ignored.
 */
export function accumulateOps(state: StrategicState, sys: number, faction: number, value: number): void {
  if (!(value > 0) || !canScore(state, faction)) return
  const cl = state.claims
  if (sys < 0 || sys >= cl.systems) return
  const m = opsCountMult(state, sys, faction)
  if (!(m > 0)) return
  cl.opsToday[sys * cl.factionCapacity + faction] += m === 1 ? value : m * value
}

/** The operations value of `credits` of trade throughput at a faction's stations (claims.json operations.tradeShare). */
export function tradeOpsValue(state: StrategicState, credits: number): number {
  return credits * state.data.charter.claims.operations.tradeShare
}

/**
 * One security sample (the slow tick, M4 spec §7.5.4): Σ strength × hp of every live patrol agent of a
 * non-outlaw faction, per system, added to `secSum`; every cell's `secSamples` counts the sample so the day's
 * mean includes the samples where a faction had nobody present.
 */
export function sampleSecurity(state: StrategicState): void {
  const cl = state.claims
  const ag = state.agents
  const hulls = state.data.hulls.hulls
  const fc = cl.factionCapacity
  for (let a = 0; a < ag.high; a++) {
    if (ag.alive[a] !== 1 || ag.role[a] !== ROLE.patrol) continue
    const f = ag.faction[a]
    if (!canScore(state, f)) continue
    const s = ag.sys[a]
    if (s >= cl.systems) continue
    cl.secSum[s * fc + f] += hulls[ag.hull[a]].strength * ag.hp[a] * securityCountMult(state, s, f)
  }
  const n = cl.systems * fc
  for (let i = 0; i < n; i++) if (cl.secSamples[i] < 0xffff) cl.secSamples[i]++
}

/**
 * Adds influence to today's accumulator. `amount` is in the source's unit (claims.json `influence`):
 * credits for `agentTrade`, `playerTrade` (1 point per `agentTradeCredits` / `playerTradeCredits`) and
 * `playerContract` (the reward, 1 point per `playerContractCredits`); a count for `restartDelivery`,
 * `relinkSponsor` and `zoneWin` (points each); points for `reputation` and `isolation` (already computed).
 * Returns the points added (0 for outlaws, unknown sources and non-positive amounts).
 */
export function addInfluence(state: StrategicState, sys: number, faction: number, amount: number, source: number): number {
  if (!(amount > 0) || !canScore(state, faction)) return 0
  const cl = state.claims
  if (sys < 0 || sys >= cl.systems) return 0
  const inf = state.data.charter.claims.influence
  let points: number
  switch (source) {
    case INFLUENCE_SOURCE.agentTrade:
      points = amount / inf.agentTradeCredits
      break
    case INFLUENCE_SOURCE.playerTrade:
      points = amount / inf.playerTradeCredits
      break
    case INFLUENCE_SOURCE.playerContract:
      points = amount / inf.playerContractCredits
      break
    case INFLUENCE_SOURCE.restartDelivery:
      points = amount * inf.restartDelivery
      break
    case INFLUENCE_SOURCE.relinkSponsor:
      points = amount * inf.relinkSponsor
      break
    case INFLUENCE_SOURCE.zoneWin:
      points = amount * inf.zoneWin
      break
    case INFLUENCE_SOURCE.reputation:
    case INFLUENCE_SOURCE.isolation:
      points = amount
      break
    default:
      return 0
  }
  cl.infToday[sys * cl.factionCapacity + faction] += points
  return points
}

/** True when `faction` has a working presence in `sys` (an operations or security EMA above 0). */
export function isPresent(state: StrategicState, sys: number, faction: number): boolean {
  const cl = state.claims
  const i = sys * cl.factionCapacity + faction
  return cl.ops[i] > 0 || cl.sec[i] > 0
}

/**
 * The daily influence sources (M5 spec §5.1): reputation (+`reputationPerDay` × max(0, mean) ÷ 100, the mean
 * standing toward the faction of the other present non-outlaw factions) and, in the Isolated Era, Holdfast's
 * `isolationHoldfastPerDay` in every system where it has a presence.
 */
function addDailyInfluence(state: StrategicState): void {
  const cl = state.claims
  const inf = state.data.charter.claims.influence
  const rel = state.relations
  const rfc = rel.factionCapacity
  const nf = state.data.charter.factionCount < cl.factionCapacity ? state.data.charter.factionCount : cl.factionCapacity
  const isolated = state.gateStage.isolation === STAGE.active
  for (let s = 0; s < cl.systems; s++) {
    for (let f = 0; f < nf; f++) {
      if (!canScore(state, f) || !isPresent(state, s, f)) continue
      let sum = 0
      let count = 0
      for (let o = 0; o < nf; o++) {
        if (o === f || !canScore(state, o) || !isPresent(state, s, o)) continue
        sum += rel.standing[o * rfc + f]
        count++
      }
      if (count > 0) {
        const mean = sum / count
        if (mean > 0) addInfluence(state, s, f, (inf.reputationPerDay * mean) / 100, INFLUENCE_SOURCE.reputation)
      }
      if (isolated && f === FACTION.holdfast) addInfluence(state, s, f, inf.isolationHoldfastPerDay, INFLUENCE_SOURCE.isolation)
    }
  }
}

/**
 * Day slice 0 (M5 spec §3.2): folds today's accumulators into the EMAs and decays influence, then clears
 * them, and updates each system's falling-trade streak from the last two day rollups (the tariff cut).
 * `seed = true` (the day-0 evaluation at seeding, M5 spec §5.3) takes today's values as the metrics directly.
 */
export function rollMetrics(state: StrategicState, seed = false): void {
  addDailyInfluence(state)
  const cl = state.claims
  const cfg = state.data.charter.claims
  const keep = seed ? 0 : cfg.emaKeep
  const infKeep = seed ? 0 : 1 - cfg.influence.decayPerDay
  const n = cl.systems * cl.factionCapacity
  for (let i = 0; i < n; i++) {
    const samples = cl.secSamples[i]
    const secToday = samples > 0 ? cl.secSum[i] / samples : 0
    cl.ops[i] = keep * cl.ops[i] + (1 - keep) * cl.opsToday[i]
    cl.sec[i] = keep * cl.sec[i] + (1 - keep) * secToday
    cl.inf[i] = infKeep * cl.inf[i] + cl.infToday[i]
    cl.opsToday[i] = 0
    cl.secSum[i] = 0
    cl.secSamples[i] = 0
    cl.infToday[i] = 0
  }
  const r = state.rollups
  if (r.length >= 2) {
    const today = r[r.length - 1].tradeVolume
    const before = r[r.length - 2].tradeVolume
    for (let s = 0; s < cl.systems && s < today.length; s++) {
      if (today[s] < before[s]) {
        if (cl.tradeFallStreak[s] < 255) cl.tradeFallStreak[s]++
      } else cl.tradeFallStreak[s] = 0
    }
  }
}
